import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { MagnifyingGlass, UserCircle, X } from "@phosphor-icons/react";

/**
 * StaffPicker — searchable assignee picker that returns ONLY real HR staff
 * records. Used by the Projects Phase 2 frontend for deliverable / task /
 * reviewer assignments so we never accept a "ghost" assignee.
 *
 * Props:
 *   value        — currently-selected staff identifier (email or id)
 *   onChange(id, staff?) — called when selection changes
 *   placeholder  — input placeholder
 *   testid       — data-testid prefix
 *   disabled     — disable selection
 *   endpoint     — defaults to /projects/_assignee-lookup; override to reuse the picker elsewhere
 */
export default function StaffPicker({
  value,
  onChange,
  placeholder = "Search by name, email, employee code…",
  testid = "staff-picker",
  disabled = false,
  endpoint = "/projects/_assignee-lookup",
}) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const ref = useRef(null);

  // If parent provides a value but we don't have a "selected" object yet, fetch it.
  useEffect(() => {
    let alive = true;
    if (!value) { setSelected(null); return; }
    if (selected && (selected.email === value || selected.id === value)) return;
    (async () => {
      try {
        const { data } = await api.get(`${endpoint}?q=${encodeURIComponent(value)}&limit=10`);
        // STRICT match only — never silently fall back to the first item.
        // If the persisted value doesn't resolve to a real staff record we
        // surface a clear "stale assignee" chip so the user knows to re-pick.
        const exact = (data.items || []).find(
          (x) => x.email === value || x.id === value || x.employee_code === value,
        );
        if (!alive) return;
        if (exact) {
          setSelected(exact);
        } else {
          setSelected({
            id: value,
            email: value,
            full_name: value,
            __stale: true,
          });
        }
      } catch {
        if (alive) setSelected({ id: value, email: value, full_name: value, __stale: true });
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Debounced live search
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`${endpoint}?q=${encodeURIComponent(q)}&limit=25`);
        setResults(data.items || []);
      } catch (e) { setResults([]); }
      finally { setLoading(false); }
    }, 180);
    return () => clearTimeout(t);
  }, [q, open, endpoint]);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const pick = (staff) => {
    setSelected(staff);
    setOpen(false);
    setQ("");
    onChange?.(staff.email || staff.id, staff);
  };

  const clear = () => {
    setSelected(null);
    setQ("");
    onChange?.("", null);
  };

  if (selected) {
    const stale = !!selected.__stale;
    return (
      <div ref={ref} className="relative">
        <div
          className={`flex items-start gap-2 rounded-md border p-2 text-sm ${
            stale ? "border-amber-300 bg-amber-50/60" : "border-blue-300 bg-blue-50/50"
          }`}
          data-testid={`${testid}-selected`}
        >
          <UserCircle size={18} weight="fill" className={`mt-0.5 flex-shrink-0 ${stale ? "text-amber-700" : "text-blue-700"}`} />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="font-semibold text-slate-900 text-sm">{selected.full_name}</span>
              {selected.employee_code && (
                <span className="font-mono-alt text-[10px] uppercase tracking-wider rounded bg-white px-1.5 py-0.5 text-slate-700 border border-slate-200">
                  {selected.employee_code}
                </span>
              )}
              {stale && (
                <span
                  className="font-mono-alt text-[9px] uppercase tracking-wider rounded bg-amber-200 text-amber-900 px-1.5 py-0.5"
                  title="No HR staff record matches this assignee — please re-pick."
                  data-testid={`${testid}-stale`}
                >
                  stale
                </span>
              )}
            </div>
            <div className="mt-0.5 truncate text-[11px] text-slate-600">{selected.email}</div>
            {(selected.designation || selected.department) && (
              <div className="mt-0.5 truncate text-[10px] uppercase tracking-wider text-slate-500">
                {selected.designation || ""}{selected.designation && selected.department && " · "}{selected.department || ""}
              </div>
            )}
          </div>
          {!disabled && (
            <button
              type="button"
              onClick={clear}
              className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full text-rose-600 hover:bg-rose-100"
              title="Change assignee"
              data-testid={`${testid}-clear`}
            >
              <X size={12} weight="bold" />
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div ref={ref} className="relative">
      <div className="relative">
        <MagnifyingGlass size={14} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          disabled={disabled}
          className="pl-8"
          data-testid={`${testid}-input`}
        />
      </div>
      {open && (
        <div className="absolute z-50 mt-1 max-h-80 w-full overflow-y-auto rounded-md border border-slate-200 bg-white shadow-lg" data-testid={`${testid}-dropdown`}>
          {loading && <div className="px-3 py-2 text-xs text-slate-500">Searching…</div>}
          {!loading && results.length === 0 && (
            <div className="px-3 py-3 text-xs text-slate-500">
              {q ? "No matching staff found." : "Type to search the HR Staff Directory…"}
            </div>
          )}
          {!loading && results.map((r) => (
            <button
              key={r.id || r.email}
              type="button"
              onClick={() => pick(r)}
              className="block w-full px-3 py-2 text-left hover:bg-slate-50 border-b border-slate-100 last:border-b-0"
              data-testid={`${testid}-opt-${r.email || r.id}`}
            >
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="font-semibold text-slate-900 text-sm">{r.full_name}</span>
                {r.employee_code && (
                  <span className="font-mono-alt text-[10px] uppercase tracking-wider rounded bg-slate-100 px-1.5 py-0.5 text-slate-600">{r.employee_code}</span>
                )}
              </div>
              <div className="mt-0.5 truncate text-[11px] text-slate-600">{r.email}</div>
              {(r.designation || r.department) && (
                <div className="mt-0.5 truncate text-[10px] uppercase tracking-wider text-slate-500">
                  {r.designation || ""}{r.designation && r.department && " · "}{r.department || ""}
                </div>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
